/**
 * Admin Moderation Review Dashboard - MVP v2
 * 
 * Enhanced with lifecycle states, assignment, and transition history.
 * 
 * Lifecycle: open → in_review → (escalated|resolved|dismissed)
 */
import React, { useEffect, useState, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../App";
import { 
  ArrowLeft, RefreshCw, Shield, User, Clock, 
  ChevronRight, XCircle, Ban, Eye, EyeOff,
  CheckCircle, AlertCircle, Loader2, UserPlus, UserMinus,
  ArrowUpCircle, PlayCircle, History, ChevronDown
} from "lucide-react";
import { toast } from "sonner";

const API = window.location.origin;

// ============================================================================
// LIFECYCLE STATES
// ============================================================================
const LIFECYCLE_STATES = ["open", "in_review", "escalated", "resolved", "dismissed"];

const STATE_CONFIG = {
  open: { color: "rose", icon: "🔴", label: "Open" },
  in_review: { color: "blue", icon: "🔍", label: "In Review" },
  escalated: { color: "orange", icon: "⚡", label: "Escalated" },
  resolved: { color: "emerald", icon: "✅", label: "Resolved" },
  dismissed: { color: "slate", icon: "⏭️", label: "Dismissed" },
};

// ============================================================================
// SEVERITY BADGE
// ============================================================================
function SeverityBadge({ severity }) {
  const styles = {
    critical: "bg-red-500/20 text-red-400 border-red-500/40",
    high: "bg-orange-500/20 text-orange-400 border-orange-500/40",
    med: "bg-yellow-500/20 text-yellow-400 border-yellow-500/40",
    low: "bg-slate-500/20 text-slate-400 border-slate-500/40",
  };
  const icons = {
    critical: "🚨",
    high: "⚠️",
    med: "🟡",
    low: "🔵",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded border font-medium ${styles[severity] || styles.low}`}>
      {icons[severity] || "🔵"} {severity?.toUpperCase() || "LOW"}
    </span>
  );
}

// ============================================================================
// SOURCE BADGE
// ============================================================================
function SourceBadge({ source }) {
  if (source === "guardian") {
    return (
      <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 border border-purple-500/40">
        🛡️ Guardian
      </span>
    );
  }
  return (
    <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-400 border border-blue-500/40">
      🚩 Report
    </span>
  );
}

// ============================================================================
// STATUS BADGE (Lifecycle State)
// ============================================================================
function StatusBadge({ status }) {
  const config = STATE_CONFIG[status] || STATE_CONFIG.open;
  const colorStyles = {
    rose: "bg-rose-500/20 text-rose-400 border-rose-500/40",
    blue: "bg-blue-500/20 text-blue-400 border-blue-500/40",
    orange: "bg-orange-500/20 text-orange-400 border-orange-500/40",
    emerald: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
    slate: "bg-slate-500/20 text-slate-400 border-slate-500/40",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded border font-medium ${colorStyles[config.color]}`}>
      {config.icon} {config.label}
    </span>
  );
}

// ============================================================================
// USER SNAPSHOT CARD
// ============================================================================
function UserSnapshotCard({ user, label }) {
  if (!user) return null;
  
  return (
    <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
      <div className="text-xs text-slate-500 mb-2">{label}</div>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-slate-400">
          <User size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-200 truncate">
            {user.first_name || "Unknown"} {user.last_name || ""}
          </div>
          <div className="text-xs text-slate-500 truncate">{user.email || user.id}</div>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-2 text-xs">
        <span className="px-2 py-0.5 bg-slate-700 rounded text-slate-300">
          {user.subscription_tier || "free"}
        </span>
        {user.identity_verified && (
          <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded">
            ✓ Verified
          </span>
        )}
        <span className="px-2 py-0.5 bg-rose-500/20 text-rose-400 rounded">
          {user.total_reports_against || 0} reports
        </span>
        <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded">
          {user.total_mod_actions || 0} actions
        </span>
      </div>
    </div>
  );
}

// ============================================================================
// TRANSITION HISTORY
// ============================================================================
function TransitionHistory({ history }) {
  const [expanded, setExpanded] = useState(false);
  
  if (!history || history.length === 0) {
    return (
      <div className="text-xs text-slate-500 italic">No transition history</div>
    );
  }
  
  const displayHistory = expanded ? history : history.slice(0, 3);
  
  return (
    <div className="space-y-2">
      {displayHistory.map((t, i) => (
        <div key={t.id || i} className="flex items-start gap-2 text-xs">
          <div className="w-1.5 h-1.5 rounded-full bg-slate-500 mt-1.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-slate-300">
              <StatusBadge status={t.from_state?.replace("assigned:", "") || "?"} />
              <span className="mx-1">→</span>
              <StatusBadge status={t.to_state?.replace("assigned:", "") || "?"} />
            </div>
            <div className="text-slate-500 mt-0.5">
              {t.admin_email || t.admin_id?.slice(0, 8)} • {new Date(t.timestamp).toLocaleString()}
            </div>
            {t.note && <div className="text-slate-400 mt-0.5 italic">&ldquo;{t.note}&rdquo;</div>}
          </div>
        </div>
      ))}
      {history.length > 3 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1"
        >
          {expanded ? "Show less" : `Show ${history.length - 3} more`}
          <ChevronDown size={12} className={expanded ? "rotate-180" : ""} />
        </button>
      )}
    </div>
  );
}

// ============================================================================
// LIFECYCLE ACTIONS
// ============================================================================
function LifecycleActions({ status, allowedTransitions, onTransition, onAssign, assignedTo, loading }) {
  const [showReasonInput, setShowReasonInput] = useState(null);
  const [reason, setReason] = useState("");
  
  const handleTransition = async (toState) => {
    if (["resolved", "dismissed"].includes(toState)) {
      if (!reason.trim()) {
        setShowReasonInput(toState);
        return;
      }
    }
    await onTransition(toState, reason);
    setReason("");
    setShowReasonInput(null);
  };
  
  const transitionButtons = [
    { state: "in_review", label: "Start Review", icon: <PlayCircle size={14} />, color: "blue" },
    { state: "escalated", label: "Escalate", icon: <ArrowUpCircle size={14} />, color: "orange" },
    { state: "resolved", label: "Resolve", icon: <CheckCircle size={14} />, color: "emerald" },
    { state: "dismissed", label: "Dismiss", icon: <XCircle size={14} />, color: "slate" },
    { state: "open", label: "Release", icon: <ArrowLeft size={14} />, color: "slate" },
  ];
  
  const colorStyles = {
    blue: "bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border-blue-500/40",
    orange: "bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 border-orange-500/40",
    emerald: "bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border-emerald-500/40",
    slate: "bg-slate-500/20 hover:bg-slate-500/30 text-slate-400 border-slate-500/40",
  };
  
  return (
    <div className="space-y-3">
      {/* Transition buttons */}
      <div className="flex flex-wrap gap-2">
        {transitionButtons
          .filter(b => allowedTransitions?.includes(b.state))
          .map(({ state, label, icon, color }) => (
            <button
              key={state}
              onClick={() => handleTransition(state)}
              disabled={loading}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-sm font-medium transition-colors disabled:opacity-50 ${colorStyles[color]}`}
              data-testid={`lifecycle-${state}`}
            >
              {icon}
              {label}
            </button>
          ))}
      </div>
      
      {/* Reason input for resolve/dismiss */}
      {showReasonInput && (
        <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">
          <label className="text-xs text-slate-500 mb-1 block">
            Reason for {showReasonInput} (required)
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why..."
            className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
            rows={2}
            data-testid="lifecycle-reason"
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => handleTransition(showReasonInput)}
              disabled={!reason.trim() || loading}
              className="px-3 py-1.5 bg-purple-500/20 text-purple-400 rounded text-sm font-medium disabled:opacity-50"
            >
              Confirm {showReasonInput}
            </button>
            <button
              onClick={() => { setShowReasonInput(null); setReason(""); }}
              className="px-3 py-1.5 bg-slate-700 text-slate-300 rounded text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {/* Assignment */}
      <div className="flex items-center gap-2 pt-2 border-t border-slate-700">
        {assignedTo ? (
          <>
            <span className="text-xs text-slate-500">Assigned to:</span>
            <span className="text-xs text-slate-300">{assignedTo}</span>
            <button
              onClick={() => onAssign(null)}
              disabled={loading}
              className="ml-auto flex items-center gap-1 px-2 py-1 text-xs bg-slate-700 hover:bg-slate-600 rounded text-slate-300"
            >
              <UserMinus size={12} />
              Unassign
            </button>
          </>
        ) : (
          <button
            onClick={() => onAssign("me")}
            disabled={loading}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-500/20 hover:bg-purple-500/30 rounded text-purple-400"
            data-testid="assign-to-me"
          >
            <UserPlus size={12} />
            Assign to me
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// MODERATION ACTIONS (Ban, Kick, etc.)
// ============================================================================
function ModerationActions({ onAction, loading, disabled }) {
  const actions = [
    { type: "kick_lounge", label: "Kick Lounge", icon: <Ban size={14} />, color: "orange" },
    { type: "mute_24h", label: "Mute 24h", icon: <EyeOff size={14} />, color: "yellow" },
    { type: "shadowban", label: "Shadowban", icon: <Eye size={14} />, color: "red" },
    { type: "ban", label: "Ban", icon: <XCircle size={14} />, color: "red" },
  ];
  
  const colorStyles = {
    orange: "bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 border-orange-500/40",
    yellow: "bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 border-yellow-500/40",
    red: "bg-red-500/20 hover:bg-red-500/30 text-red-400 border-red-500/40",
  };
  
  return (
    <div className="flex flex-wrap gap-2">
      {actions.map(({ type, label, icon, color }) => (
        <button
          key={type}
          onClick={() => onAction(type)}
          disabled={loading || disabled}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${colorStyles[color]}`}
          data-testid={`mod-action-${type}`}
        >
          {icon}
          {label}
        </button>
      ))}
    </div>
  );
}

// ============================================================================
// DETAIL PANEL
// ============================================================================
function DetailPanel({ item, detail, loading, onAction, onTransition, onAssign, onClose, currentUserId }) {
  const [note, setNote] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  
  const handleAction = async (actionType) => {
    if (!note.trim() || note.trim().length < 5) {
      toast.error("Note is required (min 5 characters)");
      return;
    }
    setActionLoading(true);
    await onAction(actionType, note);
    setActionLoading(false);
    setNote("");
  };
  
  const handleTransition = async (toState, reason) => {
    setActionLoading(true);
    await onTransition(toState, reason);
    setActionLoading(false);
  };
  
  const handleAssign = async (assignTo) => {
    setActionLoading(true);
    // "me" means current user
    const targetId = assignTo === "me" ? currentUserId : assignTo;
    await onAssign(targetId);
    setActionLoading(false);
  };
  
  if (!item) {
    return (
      <div className="h-full flex items-center justify-center text-slate-500">
        <div className="text-center">
          <Shield size={48} className="mx-auto mb-4 opacity-50" />
          <p>Select an item from the queue to review</p>
        </div>
      </div>
    );
  }
  
  const isTerminalState = ["resolved", "dismissed"].includes(detail?.status || item.status);
  
  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-700">
        <div className="flex items-center gap-2 flex-wrap">
          <SeverityBadge severity={item.severity} />
          <SourceBadge source={item.source} />
          <StatusBadge status={detail?.status || item.status} />
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-200">
          <XCircle size={20} />
        </button>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="animate-spin text-slate-500" size={24} />
          </div>
        ) : detail ? (
          <>
            {/* Assignment Info */}
            {detail.assigned_to && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-2 text-sm">
                <span className="text-blue-400">Assigned to:</span>{" "}
                <span className="text-slate-200">{detail.assigned_to_email || detail.assigned_to}</span>
              </div>
            )}
            
            {/* Reason */}
            <div>
              <div className="text-xs text-slate-500 mb-1">Reason / Category</div>
              <div className="text-slate-200 font-medium">{detail.reason || item.reason}</div>
            </div>
            
            {/* Evidence */}
            <div>
              <div className="text-xs text-slate-500 mb-1">Evidence</div>
              <div className="bg-slate-800 rounded-lg p-3 text-sm text-slate-300 whitespace-pre-wrap border border-slate-700 max-h-48 overflow-y-auto">
                {detail.full_evidence || item.evidence_preview || "No evidence provided"}
              </div>
            </div>
            
            {/* Resolution reason if resolved/dismissed */}
            {detail.resolution_reason && (
              <div>
                <div className="text-xs text-slate-500 mb-1">Resolution Reason</div>
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2 text-sm text-emerald-300">
                  {detail.resolution_reason}
                </div>
              </div>
            )}
            
            {/* Users */}
            <div className="grid grid-cols-1 gap-3">
              <UserSnapshotCard user={detail.subject_user} label="Subject User" />
              {detail.reporter_user && (
                <UserSnapshotCard user={detail.reporter_user} label="Reporter" />
              )}
            </div>
            
            {/* Related Entity */}
            {detail.related_entity && (
              <div>
                <div className="text-xs text-slate-500 mb-1">Related {detail.related_entity.type}</div>
                <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">
                  <div className="text-xs text-slate-400">ID: {detail.related_entity.id}</div>
                  {detail.related_entity.recent_messages && (
                    <div className="mt-2 space-y-2">
                      <div className="text-xs text-slate-500">Recent Messages:</div>
                      {detail.related_entity.recent_messages.slice(0, 5).map((msg, i) => (
                        <div key={i} className="text-xs bg-slate-900 rounded p-2">
                          <span className="text-slate-500">{msg.sender_id?.slice(0, 8)}:</span>{" "}
                          <span className="text-slate-300">{msg.content}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Timestamps */}
            <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
              <div className="flex items-center gap-1">
                <Clock size={12} />
                Created: {new Date(item.created_at).toLocaleString()}
              </div>
              {detail.first_viewed_at && (
                <div className="flex items-center gap-1">
                  <Eye size={12} />
                  First viewed: {new Date(detail.first_viewed_at).toLocaleString()}
                </div>
              )}
              {detail.resolved_at && (
                <div className="flex items-center gap-1">
                  <CheckCircle size={12} />
                  Resolved: {new Date(detail.resolved_at).toLocaleString()}
                </div>
              )}
            </div>
            
            {/* Transition History */}
            <div>
              <div className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                <History size={12} />
                Transition History
              </div>
              <TransitionHistory history={detail.transition_history} />
            </div>
          </>
        ) : (
          <div className="text-slate-500">No details available</div>
        )}
      </div>
      
      {/* Action Footer */}
      {!isTerminalState && (
        <div className="p-4 border-t border-slate-700 space-y-4">
          {/* Lifecycle Actions */}
          <div>
            <div className="text-xs text-slate-500 mb-2">Lifecycle Actions</div>
            <LifecycleActions
              status={detail?.status || item.status}
              allowedTransitions={detail?.allowed_transitions || []}
              onTransition={handleTransition}
              onAssign={handleAssign}
              assignedTo={detail?.assigned_to_email || detail?.assigned_to}
              loading={actionLoading}
            />
          </div>
          
          {/* Moderation Actions */}
          {detail?.subject_user_id && (
            <div>
              <div className="text-xs text-slate-500 mb-2">Moderation Actions (applies punishment)</div>
              <div>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Note required for moderation actions..."
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 mb-2"
                  rows={2}
                  data-testid="mod-action-note"
                />
                <ModerationActions
                  onAction={handleAction}
                  loading={actionLoading}
                  disabled={!detail?.subject_user_id}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================
export default function AdminModerationPage() {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [statusFilter, setStatusFilter] = useState("open");
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  
  const isAdmin = user?.is_admin;
  
  // Load queue
  const loadQueue = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/moderation/queue?status=${statusFilter}&limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) {
        toast.error("Admin access required");
        return;
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Failed to load queue");
      setQueue(data);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter]);
  
  // Load stats
  const loadStats = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/admin/moderation/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (e) {
      console.error("Failed to load stats:", e);
    }
  }, [token]);
  
  // Load item detail
  const loadDetail = useCallback(async (item) => {
    if (!token || !item) return;
    setDetailLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/moderation/item/${item.source}/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Failed to load detail");
      setSelectedDetail(data);
    } catch (e) {
      toast.error(e.message);
      setSelectedDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [token]);
  
  // Execute moderation action
  const handleAction = async (actionType, note) => {
    if (!token || !selectedItem) return;
    
    const targetUserId = selectedDetail?.subject_user_id || selectedItem.subject_user_id;
    if (!targetUserId && actionType !== "dismiss") {
      toast.error("No target user identified");
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/admin/moderation/action`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          source: selectedItem.source,
          item_id: selectedItem.id,
          action_type: actionType,
          target_user_id: targetUserId || "unknown",
          note: note,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Action failed");
      
      toast.success(`Action "${actionType}" completed: ${data.result}`);
      
      // Refresh
      await loadQueue();
      await loadStats();
      setSelectedItem(null);
      setSelectedDetail(null);
    } catch (e) {
      toast.error(e.message);
    }
  };
  
  // Transition lifecycle state
  const handleTransition = async (toState, reason) => {
    if (!token || !selectedItem) return;
    
    try {
      const res = await fetch(`${API}/api/admin/moderation/transition`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          source: selectedItem.source,
          item_id: selectedItem.id,
          to_state: toState,
          reason: reason || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Transition failed");
      
      toast.success(`Transitioned to "${toState}"`);
      
      // Refresh detail
      await loadDetail(selectedItem);
      await loadQueue();
      await loadStats();
    } catch (e) {
      toast.error(e.message);
    }
  };
  
  // Assign/unassign
  const handleAssign = async (assignTo) => {
    if (!token || !selectedItem) return;
    
    try {
      const res = await fetch(`${API}/api/admin/moderation/assign`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          source: selectedItem.source,
          item_id: selectedItem.id,
          assign_to: assignTo,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Assignment failed");
      
      toast.success(assignTo ? `Assigned to ${data.assigned_to_email || assignTo}` : "Unassigned");
      
      // Refresh detail
      await loadDetail(selectedItem);
      await loadQueue();
    } catch (e) {
      toast.error(e.message);
    }
  };
  
  // Initial load
  useEffect(() => {
    loadQueue();
    loadStats();
  }, [loadQueue, loadStats]);
  
  // Load detail when item selected
  useEffect(() => {
    if (selectedItem) {
      loadDetail(selectedItem);
    }
  }, [selectedItem, loadDetail]);
  
  // Access check
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
          <h1 className="text-xl font-bold mb-2">Access Denied</h1>
          <p className="text-slate-400">Admin access required</p>
          <button
            onClick={() => navigate("/admin")}
            className="mt-4 px-4 py-2 bg-slate-800 rounded-lg hover:bg-slate-700"
          >
            Back to Admin
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="admin-moderation-page">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/admin")}
              className="text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <Shield size={20} className="text-purple-400" />
                Moderation Review
              </h1>
              <p className="text-xs text-slate-500">Lifecycle workflow: open → review → resolve</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Stats */}
            {stats?.status_breakdown && (
              <div className="hidden sm:flex items-center gap-3 text-xs">
                <span className="text-rose-400">{stats.status_breakdown.open || 0} open</span>
                <span className="text-blue-400">{stats.status_breakdown.in_review || 0} review</span>
                <span className="text-orange-400">{stats.status_breakdown.escalated || 0} escalated</span>
                <span className="text-emerald-400">{stats.actioned_today || 0} today</span>
              </div>
            )}
            
            {/* Refresh */}
            <button
              onClick={() => { loadQueue(); loadStats(); }}
              disabled={loading}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto flex h-[calc(100vh-64px)]">
        {/* Left: Queue Table */}
        <div className="w-1/2 border-r border-slate-800 flex flex-col">
          {/* Filter Tabs */}
          <div className="flex gap-2 p-3 border-b border-slate-800 flex-wrap">
            {["open", "in_review", "escalated", "resolved", "dismissed", "all"].map((status) => {
              const config = STATE_CONFIG[status] || { icon: "📋", label: status };
              return (
                <button
                  key={status}
                  onClick={() => { setStatusFilter(status); setSelectedItem(null); }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                    statusFilter === status
                      ? "bg-purple-500/20 text-purple-400 border border-purple-500/40"
                      : "text-slate-400 hover:bg-slate-800"
                  }`}
                  data-testid={`filter-${status}`}
                >
                  {status !== "all" && <span className="text-xs">{config.icon}</span>}
                  {status === "all" ? "All" : config.label}
                  {stats?.status_breakdown && status !== "all" && (
                    <span className="text-xs opacity-60">({stats.status_breakdown[status] || 0})</span>
                  )}
                </button>
              );
            })}
          </div>
          
          {/* Queue List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="animate-spin text-slate-500" size={24} />
              </div>
            ) : queue.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-slate-500">
                <div className="text-center">
                  <CheckCircle size={32} className="mx-auto mb-2 text-emerald-500" />
                  <p>No items in queue</p>
                </div>
              </div>
            ) : (
              <div className="divide-y divide-slate-800">
                {queue.map((item) => (
                  <button
                    key={`${item.source}-${item.id}`}
                    onClick={() => setSelectedItem(item)}
                    className={`w-full text-left p-3 hover:bg-slate-900 transition-colors ${
                      selectedItem?.id === item.id ? "bg-slate-800/50" : ""
                    }`}
                    data-testid={`queue-item-${item.id}`}
                  >
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <SeverityBadge severity={item.severity} />
                      <SourceBadge source={item.source} />
                      <StatusBadge status={item.status} />
                      {item.assigned_to_email && (
                        <span className="text-xs px-2 py-0.5 rounded bg-blue-500/10 text-blue-400">
                          📌 {item.assigned_to_email.split("@")[0]}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-200 truncate">
                          {item.reason || "No reason"}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {item.evidence_preview || "No evidence"}
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-slate-500 flex-shrink-0 ml-2" />
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                      <Clock size={10} />
                      {new Date(item.created_at).toLocaleDateString()}
                      {item.subject_user_id && (
                        <>
                          <span>•</span>
                          <User size={10} />
                          {item.subject_user_id.slice(0, 8)}...
                        </>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Right: Detail Panel */}
        <div className="w-1/2 bg-slate-900/50">
          <DetailPanel
            item={selectedItem}
            detail={selectedDetail}
            loading={detailLoading}
            onAction={handleAction}
            onTransition={handleTransition}
            onAssign={handleAssign}
            onClose={() => { setSelectedItem(null); setSelectedDetail(null); }}
            currentUserId={user?.id}
          />
        </div>
      </div>
    </div>
  );
}
